import { useSyncExternalStore } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  clockDurationMinutes,
  timeEntryDayClipboard,
  type ListTimeEntriesInput,
  type TimeEntryDayClipboard
} from '@coneno/c-sheet-shared'
import { readStorage, writeStorage } from '@/lib/storage'
import { useTRPC } from '@/lib/trpc'

export function useTimeEntries(input: ListTimeEntriesInput = {}) {
  const trpc = useTRPC()
  return useQuery(trpc.timeEntries.list.queryOptions(input))
}

export function useRecentDescriptions() {
  const trpc = useTRPC()
  return useQuery(trpc.timeEntries.recentDescriptions.queryOptions())
}

// Every change can affect the entry list and the recent descriptions.
function useInvalidateTimeEntries() {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  return () => queryClient.invalidateQueries(trpc.timeEntries.pathFilter())
}

export function useCreateTimeEntry() {
  const trpc = useTRPC()
  const invalidate = useInvalidateTimeEntries()
  return useMutation(trpc.timeEntries.create.mutationOptions({ onSuccess: invalidate }))
}

export function useCopyTimeEntries() {
  const trpc = useTRPC()
  const invalidate = useInvalidateTimeEntries()
  return useMutation(trpc.timeEntries.createMany.mutationOptions({ onSuccess: invalidate }))
}

export function useUpdateTimeEntry() {
  const trpc = useTRPC()
  const invalidate = useInvalidateTimeEntries()
  return useMutation(trpc.timeEntries.update.mutationOptions({ onSuccess: invalidate }))
}

export function useDeleteTimeEntries() {
  const trpc = useTRPC()
  const invalidate = useInvalidateTimeEntries()
  return useMutation(trpc.timeEntries.deleteMany.mutationOptions({ onSuccess: invalidate }))
}

export function useRestoreTimeEntries() {
  const trpc = useTRPC()
  const invalidate = useInvalidateTimeEntries()
  return useMutation(trpc.timeEntries.restore.mutationOptions({ onSuccess: invalidate }))
}

// The copied day is kept in this browser, so it survives reloads and is shared between tabs.
const clipboardKey = 'c-sheet:day-clipboard'
const clipboardListeners = new Set<() => void>()
let memoryDay: string | null = null

function writeStoredDay(payload: TimeEntryDayClipboard): void {
  memoryDay = JSON.stringify(payload)
  writeStorage(clipboardKey, memoryDay)
  clipboardListeners.forEach((listener) => listener())
}

function subscribe(listener: () => void): () => void {
  clipboardListeners.add(listener)
  const onStorage = (event: StorageEvent) => {
    if (event.key === clipboardKey) listener()
  }
  window.addEventListener('storage', onStorage)
  return () => {
    clipboardListeners.delete(listener)
    window.removeEventListener('storage', onStorage)
  }
}

function parseDay(serialized: string | null): TimeEntryDayClipboard | null {
  if (!serialized) return null
  try {
    const result = timeEntryDayClipboard.safeParse(JSON.parse(serialized))
    return result.success ? result.data : null
  } catch {
    return null
  }
}

export type DayClipboardStatus =
  | { canPaste: false }
  | {
      canPaste: true
      sourceDate: string
      entryCount: number
      totalMinutes: number
      payload: TimeEntryDayClipboard
    }

export function useDayClipboard(): DayClipboardStatus {
  const serialized = useSyncExternalStore(subscribe, () => readStorage(clipboardKey) ?? memoryDay)
  const payload = parseDay(serialized)
  if (!payload) return { canPaste: false }
  return {
    canPaste: true,
    sourceDate: payload.source.workDate,
    entryCount: payload.entries.length,
    totalMinutes: payload.entries.reduce(
      (sum, entry) => sum + (clockDurationMinutes(entry.startTime, entry.endTime) ?? 0),
      0
    ),
    payload
  }
}

export function useCopyDayToClipboard() {
  const trpc = useTRPC()
  return useMutation(trpc.dayClipboard.copy.mutationOptions({ onSuccess: writeStoredDay }))
}

export function usePasteDayFromClipboard() {
  const trpc = useTRPC()
  const invalidate = useInvalidateTimeEntries()
  return useMutation(trpc.dayClipboard.paste.mutationOptions({ onSuccess: invalidate }))
}
